import { getStoredLegacyToken } from './legacyAuthService';

export interface AdminCategory {
  id: number;
  name: string;
  englishLabel: string;
}

function buildAuthHeaders(): HeadersInit {
  const rawToken =
    getStoredLegacyToken()
    || localStorage.getItem('legacy_token')
    || sessionStorage.getItem('legacy_token')
    || '';
  const token = rawToken
    ? (/^Bearer\s+/i.test(rawToken) ? rawToken : `Bearer ${rawToken}`)
    : '';
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...(token ? { Authorization: token } : {}),
  };
}

async function parseJsonSafe<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text.trim()) return [] as T;
  return JSON.parse(text) as T;
}

function normalizeCategoryRow(row: unknown): AdminCategory | null {
  const source = row && typeof row === 'object' ? row as Record<string, unknown> : {};
  const id = Number(source.id || 0);
  const name = String(source.name || source.nome || '').trim();
  const englishLabel = String(source.englishLabel || source.nameEn || source.labelEn || '').trim();
  if (!Number.isFinite(id) || id <= 0 || !name) return null;
  return { id, name, englishLabel };
}

function extractCategoryIdFromResponseText(text: string): number | null {
  const trimmed = String(text || '').trim();
  if (!trimmed) return null;
  try {
    const parsed = JSON.parse(trimmed) as Record<string, any>;
    const id = Number(parsed?.id || parsed?.categoryId || parsed?.data?.id || 0);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export async function listAdminCategories(): Promise<AdminCategory[]> {
  const response = await fetch('/api/bot-domains/category/list', {
    method: 'GET',
    headers: buildAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(`Errore caricamento categorie (${response.status})`);
  }
  const payload = await parseJsonSafe<unknown>(response);
  const rows = Array.isArray(payload) ? payload : [];
  return rows.map(normalizeCategoryRow).filter(Boolean) as AdminCategory[];
}

export async function createAdminCategory(name: string, englishLabel = ''): Promise<void> {
  const safeName = String(name || '').trim();
  const safeEnglishLabel = String(englishLabel || '').trim();
  if (!safeName) {
    throw new Error('Nome categoria obbligatorio');
  }
  const response = await fetch('/api/bot-domains/category', {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({ name: safeName, logo: '', englishLabel: safeEnglishLabel }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Errore creazione categoria (${response.status})`);
  }
  if (!safeEnglishLabel) return;

  const responseText = await response.text().catch(() => '');
  const createdId = extractCategoryIdFromResponseText(responseText);
  if (createdId) {
    await updateAdminCategory(createdId, safeName, safeEnglishLabel);
    return;
  }

  const rows = await listAdminCategories();
  const fallbackRow = rows
    .filter((row) => row.name === safeName)
    .sort((a, b) => b.id - a.id)[0];
  if (fallbackRow) {
    await updateAdminCategory(fallbackRow.id, safeName, safeEnglishLabel);
  }
}

export async function updateAdminCategory(id: number, name: string, englishLabel = ''): Promise<void> {
  const safeId = Number(id || 0);
  const safeName = String(name || '').trim();
  const safeEnglishLabel = String(englishLabel || '').trim();
  if (!Number.isFinite(safeId) || safeId <= 0 || !safeName) {
    throw new Error('Dati categoria non validi');
  }
  const response = await fetch('/api/bot-domains/category', {
    method: 'PUT',
    headers: buildAuthHeaders(),
    body: JSON.stringify({ id: safeId, name: safeName, englishLabel: safeEnglishLabel }),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Errore aggiornamento categoria (${response.status})`);
  }
}

export async function deleteAdminCategory(id: number): Promise<void> {
  const safeId = Number(id || 0);
  if (!Number.isFinite(safeId) || safeId <= 0) {
    throw new Error('ID categoria non valido');
  }
  const response = await fetch(`/api/bot-domains/category/${safeId}`, {
    method: 'DELETE',
    headers: buildAuthHeaders(),
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Errore eliminazione categoria (${response.status})`);
  }
}
